import { Button, Group, Stack, Switch, Text, Textarea } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconCopy, IconDownload } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { CommentListQuery } from '@/__generated__/graphql';

type ExportComment = CommentListQuery['comments'][number];

const oneLine = (s: string) => s.replace(/[\r\n]+/g, ' ').trim();

const formatLine = (c: ExportComment) =>
  `${oneLine(c.customer.name)} | ${oneLine(c.body)} | ${dayjs(c.createdAt).format('DD.MM.YYYY HH:mm:ss')} | ${c.commentId} | [${c.id}]`;

export const ExportToWheelModal = ({ comments }: { comments: ExportComment[] }) => {
  const [dedupe, setDedupe] = useState(true);

  const lines = useMemo(() => {
    const source = dedupe
      ? Array.from(
          comments
            .reduce((acc, c) => {
              if (!acc.has(c.customer.id)) acc.set(c.customer.id, c);
              return acc;
            }, new Map<string, ExportComment>())
            .values()
        )
      : comments;

    return source.map((c) => formatLine(c));
  }, [comments, dedupe]);

  const content = lines.join('\n');

  const handleDownload = () => {
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `wheelofnames-${dayjs().format('YYYYMMDD-HHmmss')}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    modals.closeAll();
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      notifications.show({ message: 'Skopírované do schránky', color: 'green' });
      modals.closeAll();
    } catch {
      notifications.show({ message: 'Nepodarilo sa skopírovať', color: 'red' });
    }
  };

  return (
    <Stack>
      <Switch
        label="Unikátni zákazníci (deduplikovať podľa zákazníka)"
        checked={dedupe}
        onChange={(e) => setDedupe(e.currentTarget.checked)}
      />
      <Textarea
        label="Náhľad"
        readOnly
        autosize
        minRows={6}
        maxRows={20}
        value={content}
        styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }}
      />
      <Group justify="space-between">
        <Text c="dimmed" size="sm">
          Záznamov: {lines.length}
        </Text>
        <Group>
          <Button
            variant="light"
            leftSection={<IconCopy size={16} />}
            onClick={handleCopy}
            disabled={lines.length === 0}
          >
            Skopírovať
          </Button>
          <Button
            leftSection={<IconDownload size={16} />}
            onClick={handleDownload}
            disabled={lines.length === 0}
          >
            Stiahnuť .txt
          </Button>
        </Group>
      </Group>
    </Stack>
  );
};
